/**
 * Weapon definitions — pure data, read by BOTH the authoritative server
 * (damage, fire rate, spread, falloff, ammo) and the client (fire cadence,
 * crosshair bloom, viewmodel). Stable ids on the wire — append, never renumber.
 *
 * Spread fields are consumed by spread.ts; angles are cone half-angles in
 * radians. All numbers are starting points to tune.
 */
export const WeaponId = {
  SMG: 0,
  Shotgun: 1,
  Rifle: 2,
  Rocket: 3,
} as const;
export type WeaponIdT = (typeof WeaponId)[keyof typeof WeaponId];

export interface Weapon {
  id: number;
  name: string;
  damage: number; // per hit; per-pellet for the shotgun
  rofMs: number; // server-enforced min interval between shots
  range: number; // max ray distance (units)
  falloffStart: number; // distance where damage begins dropping
  falloffEnd: number; // distance at/after which damage = falloffMin
  falloffMin: number; // damage multiplier at long range (0..1)
  headMult: number; // headshot damage multiplier
  magSize: number;
  reloadMs: number;
  pellets: number; // rays per trigger pull (1 except shotgun)
  hitscan: boolean;
  // --- bloom / spread (see spread.ts) ---
  baseSpread: number; // cone half-angle when perfectly settled (rad)
  maxSpread: number; // cone half-angle at full bloom (rad)
  bloomMove: number; // bloom floor (0..1) at bloomRefSpeed while moving
  bloomAir: number; // bloom floor (0..1) while airborne
  bloomPerShot: number; // bloom added per shot (0..1)
  bloomRefSpeed: number; // movement speed (u/s) mapping to full bloomMove
  crouchMul: number; // multiplier on the movement bloom floor while crouched
  recoverRate: number; // exponential bloom recovery rate (per second)
  adsSpreadMul: number; // cone multiplier while aiming down sights
  /** Suppression added to a target per near-missing pellet (0..1). */
  suppressPerNearMiss: number;
  // --- projectile weapons only (hitscan: false) ---
  projectileSpeed?: number; // units/sec
  splashRadius?: number; // explosion radius (units)
  splashDamage?: number; // max damage at the explosion centre
}

export const WEAPONS: Record<number, Weapon> = {
  [WeaponId.SMG]: {
    id: WeaponId.SMG,
    name: 'SMG',
    damage: 22,
    rofMs: 85,
    range: 120,
    falloffStart: 25,
    falloffEnd: 60,
    falloffMin: 0.4,
    headMult: 1.5,
    magSize: 30,
    reloadMs: 1800,
    pellets: 1,
    hitscan: true,
    baseSpread: 0.006,
    maxSpread: 0.1,
    bloomMove: 0.5,
    bloomAir: 0.9,
    bloomPerShot: 0.05,
    bloomRefSpeed: 8,
    crouchMul: 0.5,
    recoverRate: 6,
    adsSpreadMul: 0.55,
    suppressPerNearMiss: 0.12,
  },
  [WeaponId.Shotgun]: {
    id: WeaponId.Shotgun,
    name: 'Shotgun',
    damage: 13, // per pellet → ~117 at point blank (9 pellets)
    rofMs: 800,
    range: 50,
    falloffStart: 8,
    falloffEnd: 28,
    falloffMin: 0.15,
    headMult: 1.3,
    magSize: 7,
    reloadMs: 2500,
    pellets: 9,
    hitscan: true,
    baseSpread: 0.05,
    maxSpread: 0.13,
    bloomMove: 0.4,
    bloomAir: 0.7,
    bloomPerShot: 0.0,
    bloomRefSpeed: 8,
    crouchMul: 0.6,
    recoverRate: 7,
    adsSpreadMul: 0.8,
    suppressPerNearMiss: 0.05,
  },
  [WeaponId.Rifle]: {
    id: WeaponId.Rifle,
    name: 'Rifle',
    damage: 40,
    rofMs: 130,
    range: 260,
    falloffStart: 60,
    falloffEnd: 140,
    falloffMin: 0.6,
    headMult: 2.0,
    magSize: 25,
    reloadMs: 2200,
    pellets: 1,
    hitscan: true,
    baseSpread: 0.002,
    maxSpread: 0.12,
    bloomMove: 0.8, // heavily punished for moving — rewards standing still
    bloomAir: 0.95,
    bloomPerShot: 0.09,
    bloomRefSpeed: 8,
    crouchMul: 0.4,
    recoverRate: 5,
    adsSpreadMul: 0.3,
    suppressPerNearMiss: 0.3,
  },
  [WeaponId.Rocket]: {
    id: WeaponId.Rocket,
    name: 'Rocket',
    damage: 110, // direct hit
    rofMs: 1200,
    range: 200,
    falloffStart: 200,
    falloffEnd: 201,
    falloffMin: 1,
    headMult: 1,
    magSize: 1,
    reloadMs: 3000,
    pellets: 1,
    hitscan: false,
    baseSpread: 0.004,
    maxSpread: 0.06,
    bloomMove: 0.5,
    bloomAir: 0.9,
    bloomPerShot: 0,
    bloomRefSpeed: 8,
    crouchMul: 0.6,
    recoverRate: 5,
    adsSpreadMul: 0.5,
    suppressPerNearMiss: 0,
    projectileSpeed: 30,
    splashRadius: 5.5,
    splashDamage: 85,
  },
};

export const WEAPON_IDS: number[] = [WeaponId.SMG, WeaponId.Shotgun, WeaponId.Rifle, WeaponId.Rocket];

/** Damage multiplier from distance falloff for a weapon. */
export function falloffMultiplier(w: Weapon, dist: number): number {
  if (dist <= w.falloffStart) return 1;
  if (dist >= w.falloffEnd) return w.falloffMin;
  const t = (dist - w.falloffStart) / (w.falloffEnd - w.falloffStart);
  return 1 + (w.falloffMin - 1) * t;
}
